import { openMessageBox } from '../components/message-box/messageBox';
import { ERROR_CODE } from '../types/error-codes';
import _ from 'lodash'
import { parse, stringify } from 'qs';
import { getVar } from './get-register-url-params';
import { linkedInCheckPassword, microsoftCheckPassword } from './sign-up';
import { getRedirectUri, getLocation, getWindow, showToast } from './iframe-compat';
import type { I18n } from 'vue-i18n';

/**
 * 获取错误码
 */
function getErrorCode(error: unknown) {
    const code =
        _.get(error, 'response.data.errcode') ||
        _.get(error, 'response.data.numeric_error_code');

    return `${code}`;
}

/**
 * 清除URL中的指定参数
 */
function clearUrlParams(paramsToRemove: string[]) {
    const url = new URL(getRedirectUri());
    const searchParams = parse(url.search.slice(1));

    // 移除指定的参数
    paramsToRemove.forEach(param => {
        delete searchParams[param];
    });

    // 重新构建URL
    const newSearch = stringify(searchParams);
    const newUrl = `${url.origin}${url.pathname}${newSearch ? '?' + newSearch : ''}${url.hash}`;

    // 更新浏览器URL而不刷新页面
    getWindow().history.replaceState({}, '', newUrl);
}

/**
 * 验证密码，邮箱账号已经存在时
 */
async function checkPassword(data: { email: string }) {
    const password = await openMessageBox({
        email: data.email,
    });
    return password;
}

/**
 * 显示错误信息
 */
function alertErrorByCode(data: { code: string | number; email: string }, i18n?: I18n) {
    const { code } = data;
    const defaultCode = 'unknown';
    const codeKey = i18n?.global.te(`us.google_login.error_code.${code}`) ? code : defaultCode;
    const message = i18n?.global.t(`us.google_login.error_code.${codeKey}`);
    showToast({
        message,
        type: 'error',
        duration: 5000,
    });
}

/**
 * 处理LinkedIn登录错误（不依赖Vue Composition API）
 */
export async function handleLinkedInError(i18n?: I18n) {
    const code = Number(getVar('errorCode'));
    const email = getVar('email');

    if (code === ERROR_CODE.LINKED_IN_NEED_PASSWORD_CONFIRM) {
        try {
            const password = await checkPassword({ email });
            if (password) {
                await linkedInCheckPassword({ password, email });
                // 清除URL中的错误参数
                clearUrlParams(['errorCode', 'email']);
                getLocation().reload();
            }
        } catch (err) {
            const errorCode = getErrorCode(err);
            alertErrorByCode({ code: errorCode, email }, i18n);
        } finally {
            clearUrlParams(['errorCode', 'email']);
        }
    }
}

/**
 * 处理Microsoft登录错误（不依赖Vue Composition API）
 */
export async function handleMicrosoftError(i18n?: I18n) {
    const code = Number(getVar('errorCode'));
    const email = getVar('email');
    if (code === ERROR_CODE.MICROSOFT_NEED_PASSWORD_CONFIRM) {
        try {
            const password = await checkPassword({ email });
            if (password) {
                await microsoftCheckPassword({ password, email });
                // 清除URL中的错误参数
                clearUrlParams(['errorCode', 'email']);
                getLocation().reload();
            }
        } catch (err) {
            const errorCode = getErrorCode(err);
            alertErrorByCode({ code: errorCode, email }, i18n);
        } finally {
            clearUrlParams(['errorCode', 'email']);
        }
    }
}

/**
 * 统一处理所有第三方登录错误
 */
export function handleThirdPartyLoginErrors(i18n?: I18n) {
    handleLinkedInError(i18n);
    handleMicrosoftError(i18n);
}
